import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="share"
//
// Mobile-first share: where the browser supports the Web Share API
// (navigator.share — mobile / secure contexts), opening the action triggers the
// native device share sheet (Messages, WhatsApp, copy link, etc.). On desktop /
// unsupported browsers it falls back to copying the URL to the clipboard and
// showing the existing "Link copied" toast — so nothing is lost there.
//
// Reusable: pass the payload via data-share-*-value attributes.
export default class extends Controller {
  static values = {
    title: String,
    text: String,
    url: String,
    copiedMsg: String,
  }

  async share(event) {
    event.preventDefault()

    // Native OS share sheet on MOBILE only. On desktop — even where the browser
    // supports navigator.share (Edge/Chrome on Windows do) — we deliberately use
    // our own branded "Share with friends" modal for a consistent, controlled
    // experience. isMobileDevice() gates on a touch-primary device so a
    // touchscreen laptop (touch + mouse) is still treated as desktop.
    if (navigator.share && this.isMobileDevice()) {
      try {
        await navigator.share({
          title: this.titleValue,
          text: this.textValue,
          url: this.urlValue,
        })
      } catch (err) {
        // The user dismissing the share sheet rejects with AbortError — that's
        // not a failure, so stay silent. Anything else is a real error: log it
        // and fall back to copying so the action still does something useful.
        if (err && err.name === "AbortError") return
        console.error("Share failed:", err)
        this.copyLink()
      }
      return
    }

    // Desktop (or no Web Share API) — open the "Share with friends" modal with
    // per-network buttons (Eventbrite-style).
    this.openShareModal()
  }

  // Touch-primary device = phone/tablet. maxTouchPoints alone is true for
  // touchscreen laptops, so also require a coarse pointer (no precise mouse) to
  // keep those on the desktop modal path.
  isMobileDevice() {
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches
    return (navigator.maxTouchPoints || 0) > 0 && coarse
  }

  copyLink() {
    if (this.copyToClipboard(this.urlValue)) {
      window.displayFlashMessage("success", this.copiedMsgValue)
    }
  }

  // ── Desktop "Share with friends" modal ────────────────────────────────────
  // Built on the fly from the title/url values so any share button (event page,
  // org page, dashboard) gets it with no extra markup. Each network button is a
  // plain share-intent URL — no SDK, no auth. The rich preview card that appears
  // once shared comes from the page's Open Graph tags (open_graph_helper.rb).
  shareTargets() {
    const url = encodeURIComponent(this.urlValue)
    const text = encodeURIComponent(this.textValue || this.titleValue || "")
    const fbAppId = "" // no Messenger app id configured → fall back to web dialog
    return [
      { key: "copy",     label: "Copy link", icon: this.iconLink(),      copy: true },
      { key: "facebook", label: "Facebook",  icon: this.iconFacebook(),  href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
      { key: "messenger",label: "Messenger", icon: this.iconMessenger(), href: `https://www.facebook.com/dialog/send?link=${url}&redirect_uri=${url}${fbAppId ? `&app_id=${fbAppId}` : ""}` },
      { key: "linkedin", label: "LinkedIn",  icon: this.iconLinkedIn(),  href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
      { key: "twitter",  label: "X",         icon: this.iconX(),         href: `https://twitter.com/intent/tweet?url=${url}&text=${text}` },
      { key: "email",    label: "Email",     icon: this.iconEmail(),     href: `mailto:?subject=${text}&body=${url}` },
    ]
  }

  openShareModal() {
    // One modal at a time.
    document.querySelector(".sg-share-modal-overlay")?.remove()

    const overlay = document.createElement("div")
    overlay.className = "sg-share-modal-overlay"
    overlay.innerHTML = `
      <div class="sg-share-modal" role="dialog" aria-modal="true" aria-label="Share with friends">
        <div class="sg-share-modal-head">
          <span class="sg-share-modal-title">Share with friends</span>
          <button type="button" class="sg-share-modal-close" aria-label="Close">&times;</button>
        </div>
        <div class="sg-share-modal-targets"></div>
      </div>`

    const targetsEl = overlay.querySelector(".sg-share-modal-targets")
    this.shareTargets().forEach((t) => {
      const btn = document.createElement(t.href ? "a" : "button")
      btn.className = "sg-share-target"
      btn.setAttribute("aria-label", t.label)
      btn.setAttribute("title", t.label)
      btn.innerHTML = `<span class="sg-share-target-icon">${t.icon}</span><span class="sg-share-target-label">${t.label}</span>`
      if (t.href) {
        btn.href = t.href
        btn.target = "_blank"
        btn.rel = "noopener noreferrer"
        btn.addEventListener("click", () => this.closeShareModal(overlay))
      } else if (t.copy) {
        btn.type = "button"
        btn.addEventListener("click", () => {
          this.copyLink()
          this.closeShareModal(overlay)
        })
      }
      targetsEl.appendChild(btn)
    })

    const close = () => this.closeShareModal(overlay)
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    overlay.querySelector(".sg-share-modal-close").addEventListener("click", close)
    this._escHandler = (e) => { if (e.key === "Escape") close() }
    document.addEventListener("keydown", this._escHandler)

    document.body.appendChild(overlay)
  }

  closeShareModal(overlay) {
    overlay?.remove()
    if (this._escHandler) {
      document.removeEventListener("keydown", this._escHandler)
      this._escHandler = null
    }
  }

  // Inline SVGs so the modal is fully self-contained (no icon-asset lookups).
  iconLink()      { return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>` }
  iconFacebook()  { return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99C18.34 21.13 22 16.99 22 12z"/></svg>` }
  iconMessenger() { return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.19.16.14.26.35.27.57l.05 1.78c.02.57.61.94 1.13.71l1.99-.88c.17-.07.35-.09.53-.04 1.19.33 2.46.5 3.79.5 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm6 7.46l-2.94 4.66c-.47.74-1.47.93-2.18.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.4c-.42.32-.97-.18-.69-.63l2.94-4.66c.47-.74 1.47-.93 2.18-.4l2.34 1.75c.21.16.51.16.72 0l3.16-2.4c.42-.32.97.18.69.63z"/></svg>` }
  iconLinkedIn()  { return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V9.75H5.67v8.59h2.67zM7 8.5a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9.84v-4.93c0-2.63-1.4-3.85-3.28-3.85-1.51 0-2.19.83-2.57 1.42v-1.23h-2.67v8.59h2.67v-4.79c0-1.26.24-2.48 1.8-2.48 1.54 0 1.56 1.44 1.56 2.56v4.71h2.66z"/></svg>` }
  iconX()         { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 1.75h3.68l-8.04 9.19L24 22.25h-7.41l-5.8-7.58-6.64 7.58H.46l8.6-9.83L0 1.75h7.6l5.24 6.93 6.06-6.93zm-1.29 18.3h2.04L6.48 3.84H4.29L17.61 20.05z"/></svg>` }
  iconEmail()     { return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>` }

  // Mirrors copy_text_controller#copyToClipboard: modern clipboard API in secure
  // contexts, with a textarea + execCommand fallback for non-secure ones.
  copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
        return true
      }

      const textArea = document.createElement("textarea")
      textArea.value = text
      textArea.style.position = "fixed"
      textArea.style.left = "-999999px"
      textArea.style.top = "-999999px"
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand("copy")
        textArea.remove()
        return true
      } catch (err) {
        console.error("Failed to copy text:", err)
        textArea.remove()
        return false
      }
    } catch (err) {
      console.error("Failed to copy text:", err)
      return false
    }
  }
}
